import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BackButton, HomeButton } from '../components/NavButtons';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  ChatCircle, PaperPlaneTilt, ArrowLeft, Check, Checks, MapPin, User,
  MagnifyingGlass, Circle
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatMsgTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Conversation list item
const ConvoItem = ({ convo, userId, isActive, onClick }) => {
  const other = Object.entries(convo.participants || {}).find(([id]) => id !== userId);
  const otherName = other ? other[1].name : 'Unknown';
  const otherRole = other ? other[1].role : '';
  const unread = convo.unread_count?.[userId] || 0;

  return (
    <button onClick={onClick}
      className={`w-full text-left p-3.5 flex items-start gap-3 transition-colors border-b border-slate-100 last:border-0 ${isActive ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
      data-testid={`convo-${convo.conversation_id}`}>
      <div className="relative shrink-0">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-base font-semibold ${isActive ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
          {otherName.charAt(0).toUpperCase()}
        </div>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm text-slate-900 truncate">{otherName}</span>
          <span className="text-[10px] text-slate-400 shrink-0">{formatTime(convo.last_message_at)}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${otherRole === 'seller' || otherRole === 'both' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
            {otherRole === 'seller' || otherRole === 'both' ? 'Seller' : otherRole === 'admin' ? 'Admin' : 'Buyer'}
          </span>
        </div>
        {convo.listing_title && (
          <p className="text-[10px] text-slate-400 truncate mt-0.5">Re: {convo.listing_title}</p>
        )}
        <p className={`text-xs mt-0.5 truncate ${unread > 0 ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
          {convo.last_message || 'No messages yet'}
        </p>
      </div>
    </button>
  );
};

// Message bubble — handles regular and system messages differently
const MsgBubble = ({ msg, isMine }) => {
  if (msg.type === 'system') {
    return (
      <div className="flex justify-center my-3" data-testid={`msg-${msg.message_id}`}>
        <div className="max-w-[85%] px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-center">
          <p className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide mb-0.5">System</p>
          <p className="text-xs text-slate-700 whitespace-pre-wrap break-words">{msg.text}</p>
          <p className="text-[10px] text-slate-400 mt-1">{formatMsgTime(msg.created_at)}</p>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`msg-${msg.message_id}`}>
      <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl ${isMine ? 'bg-blue-500 text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'}`}>
        <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
        <div className={`flex items-center gap-1 mt-0.5 ${isMine ? 'justify-end' : ''}`}>
          <span className={`text-[10px] ${isMine ? 'text-blue-200' : 'text-slate-400'}`}>{formatMsgTime(msg.created_at)}</span>
          {isMine && (msg.read ? <Checks size={12} className="text-blue-200" /> : <Check size={12} className="text-blue-300" />)}
        </div>
      </div>
    </div>
  );
};

// Quick-reply chip strip — shown above the input when the chat is empty or short.
const QUICK_REPLIES_BUYER = [
  'Is this still available?',
  'What is your best price?',
  'Can I visit today?',
  'Can you share more photos?',
];
const QUICK_REPLIES_SELLER = [
  'Yes, still available!',
  'Sorry, already booked.',
  'Sure, feel free to visit.',
  'Sending photos shortly.',
];
const QuickReplies = ({ onPick, viewerIsSeller }) => {
  const chips = viewerIsSeller ? QUICK_REPLIES_SELLER : QUICK_REPLIES_BUYER;
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="quick-replies-strip">
      {chips.map(c => (
        <button key={c} onClick={() => onPick(c)}
          className="shrink-0 text-[11px] px-3 h-7 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          data-testid={`quick-reply-${c.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}>
          {c}
        </button>
      ))}
    </div>
  );
};

const ChatPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [showMobileList, setShowMobileList] = useState(true);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-start conversation from listing page
  const startSellerId = searchParams.get('seller_id');
  const startListingId = searchParams.get('listing_id');
  const ctxService = searchParams.get('service');
  const ctxVariant = searchParams.get('variant');
  const ctxDate = searchParams.get('date');

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chat/conversations`);
      setConversations(res.data.conversations || []);
    } catch (err) { console.error('Fetch convos error:', err); }
  }, []);

  const fetchMessages = useCallback(async (convoId) => {
    if (!convoId) return;
    try {
      const res = await axios.get(`${API_URL}/api/chat/messages/${convoId}`);
      setMessages(res.data.messages || []);
      // Mark as read
      await axios.put(`${API_URL}/api/chat/messages/read/${convoId}`, {});
      // Refresh convo list to update unread counts
      fetchConversations();
    } catch (err) { console.error('Fetch messages error:', err); }
  }, [fetchConversations]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      await fetchConversations();
      // If arriving from listing page with seller_id, auto-create/get conversation
      if (startSellerId) {
        try {
          const res = await axios.post(`${API_URL}/api/chat/conversations`, {
            other_user_id: startSellerId,
            listing_id: startListingId || null,
          });
          setActiveConvoId(res.data.conversation_id);
          setShowMobileList(false);
          await fetchConversations();
          // Prefill a smart first-draft message that references the listing +
          // any service/variant the buyer had selected before tapping Contact.
          const listingTitle = res.data.listing_title || 'this space';
          const bits = [];
          if (ctxService) bits.push(`the "${ctxService}" service`);
          else if (ctxVariant) bits.push(`the "${ctxVariant}" option`);
          if (ctxDate) bits.push(`for ${ctxDate}`);
          const suffix = bits.length ? ` I'd like to ask about ${bits.join(' ')}.` : '';
          setNewMsg(`Hi! I'm interested in ${listingTitle}.${suffix}`);
        } catch (err) { console.error('Auto-create convo error:', err); }
      }
      setLoading(false);
    };
    init();
  }, [startSellerId, startListingId, ctxService, ctxVariant, ctxDate, fetchConversations]);

  // Fetch messages when active convo changes
  useEffect(() => {
    if (activeConvoId) fetchMessages(activeConvoId);
  }, [activeConvoId, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Polling for new messages
  useEffect(() => {
    if (!activeConvoId) return;
    pollRef.current = setInterval(() => {
      fetchMessages(activeConvoId);
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeConvoId, fetchMessages]);

  const handleSend = async () => {
    const text = newMsg.trim();
    if (!text || !activeConvoId) return;
    setSending(true);
    setNewMsg('');
    try {
      await axios.post(`${API_URL}/api/chat/messages`, {
        conversation_id: activeConvoId,
        text,
      });
      await fetchMessages(activeConvoId);
      inputRef.current?.focus();
    } catch (err) { console.error('Send error:', err); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const openConvo = (convoId) => {
    setActiveConvoId(convoId);
    setShowMobileList(false);
  };

  const activeConvo = conversations.find(c => c.conversation_id === activeConvoId);
  const otherParticipant = activeConvo
    ? Object.entries(activeConvo.participants || {}).find(([id]) => id !== user?.user_id)
    : null;
  const otherName = otherParticipant ? otherParticipant[1].name : '';

  const filteredConvos = conversations.filter(c => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    const names = Object.values(c.participants || {}).map(p => p.name.toLowerCase());
    return names.some(n => n.includes(q)) || (c.listing_title || '').toLowerCase().includes(q);
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700 mb-4">Login to access messages</h2>
          <Button onClick={() => navigate('/login')} className="rounded-full">Login</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50" data-testid="chat-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shrink-0">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-2">
          {/* Mobile: back to list */}
          {!showMobileList && activeConvoId && (
            <button onClick={() => { setShowMobileList(true); setActiveConvoId(null); }}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-full" data-testid="chat-back-to-list">
              <ArrowLeft size={20} className="text-slate-700" />
            </button>
          )}
          <BackButton />
          <HomeButton />
          <h1 className="font-semibold text-slate-900 text-lg">Messages</h1>
          {conversations.length > 0 && (
            <span className="text-xs text-slate-400 ml-1">({conversations.length})</span>
          )}
        </div>
      </header>

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden max-w-7xl mx-auto w-full">
        {/* Conversation List */}
        <div className={`w-full lg:w-80 lg:border-r border-slate-200 bg-white flex flex-col shrink-0 ${!showMobileList && activeConvoId ? 'hidden lg:flex' : 'flex'}`}
          data-testid="convo-list-panel">
          {/* Search */}
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search conversations..."
                className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:border-blue-400 outline-none"
                data-testid="convo-search-input" />
            </div>
          </div>
          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConvos.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <ChatCircle size={48} className="text-slate-300 mb-3" />
                <p className="text-sm font-medium text-slate-500">No conversations yet</p>
                <p className="text-xs text-slate-400 mt-1">Start one by clicking &ldquo;Contact&rdquo; on any listing</p>
              </div>
            ) : (
              filteredConvos.map(c => (
                <ConvoItem key={c.conversation_id} convo={c} userId={user.user_id}
                  isActive={c.conversation_id === activeConvoId} onClick={() => openConvo(c.conversation_id)} />
              ))
            )}
          </div>
        </div>

        {/* Message Thread */}
        <div className={`flex-1 flex flex-col bg-slate-50 ${showMobileList && !activeConvoId ? 'hidden lg:flex' : 'flex'}`}
          data-testid="message-thread-panel">
          {!activeConvoId ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <ChatCircle size={64} className="text-slate-200 mb-4" />
              <h3 className="text-lg font-semibold text-slate-500">Select a conversation</h3>
              <p className="text-sm text-slate-400 mt-1">Choose from your conversations or start a new one from a listing</p>
            </div>
          ) : (
            <>
              {/* Smart Context Bar — listing card + jump-back-to-book shortcut */}
              <div className="bg-white border-b border-slate-200 shrink-0" data-testid="thread-header">
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                    {otherName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">{otherName}</p>
                    <p className="text-[10px] text-slate-400">
                      {Object.values(activeConvo?.participants || {}).find(p => ['seller', 'both', 'admin'].includes(p.role)) ? 'Host' : 'Buyer'}
                    </p>
                  </div>
                </div>
                {activeConvo?.listing_id && (
                  <div className="px-4 pb-3 flex items-center gap-3 border-t border-slate-100 pt-2.5" data-testid="chat-listing-context">
                    {activeConvo.listing_image && (
                      <img
                        src={activeConvo.listing_image.startsWith('http') ? activeConvo.listing_image : `${API_URL}${activeConvo.listing_image}`}
                        alt={activeConvo.listing_title || 'listing'}
                        className="w-12 h-12 rounded-lg object-cover shrink-0 border border-slate-200"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Chatting about</p>
                      <p className="text-sm font-semibold text-slate-900 truncate">{activeConvo.listing_title || 'Listing'}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => navigate(`/listing/${activeConvo.listing_id}`)}
                        className="text-[11px] font-medium px-3 h-8 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full"
                        data-testid="view-listing-from-chat">
                        View
                      </button>
                      <button onClick={() => navigate(`/listing/${activeConvo.listing_id}#booking-sidebar`)}
                        className="text-[11px] font-semibold px-3 h-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                        data-testid="book-now-from-chat">
                        Book Now
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm text-slate-400">No messages yet. Say hello!</p>
                  </div>
                ) : (
                  <>
                    {messages.map(msg => (
                      <MsgBubble key={msg.message_id} msg={msg} isMine={msg.sender_id === user.user_id} />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
              <div className="bg-white border-t border-slate-200 p-3 shrink-0" data-testid="message-input-area">
                <div className="max-w-4xl mx-auto space-y-2">
                  {messages.filter(m => m.type !== 'system').length <= 3 && (
                    <QuickReplies
                      viewerIsSeller={user?.role === 'seller' || user?.role === 'both' || user?.role === 'admin'}
                      onPick={(text) => setNewMsg(prev => prev.trim() ? prev : text)}
                    />
                  )}
                  <div className="flex items-end gap-2">
                    <textarea ref={inputRef} value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
                      onKeyDown={handleKeyDown} placeholder="Type a message..."
                      rows={1}
                      className="flex-1 resize-none px-4 py-2.5 border border-slate-200 rounded-2xl text-sm bg-slate-50 focus:bg-white focus:border-blue-400 outline-none max-h-32"
                      style={{ minHeight: '42px' }}
                      data-testid="message-text-input" />
                    <button onClick={handleSend} disabled={!newMsg.trim() || sending}
                      className="w-10 h-10 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white rounded-full flex items-center justify-center shrink-0 transition-colors"
                      data-testid="send-message-btn">
                      <PaperPlaneTilt size={18} weight="fill" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
